import React, { useId, useState } from 'react';
import { CheckCircle2, XCircle, Check, ChevronDown, Lightbulb } from 'lucide-react';
import { RenderedHtml } from 'modules/editor';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * The answer-review building blocks every result surface shares, so a correct
 * option looks the same on a single question and inside a reading passage.
 * Styling comes from the `.ts-review-*` classes (`AnswerReviewStyles` / TestResults).
 */

export type ReviewState = 'correct' | 'incorrect' | 'empty';

interface ReviewOptionProps {
    html: string;
    isCorrect: boolean;
    isSelected: boolean;
    multi: boolean;
    /** The answer key was withheld: show only what the learner picked, with no right/wrong colouring. */
    keyHidden?: boolean;
}

/** One choice: the key is green, a wrong pick is red, everything else stays neutral. */
export const ReviewOption: React.FC<ReviewOptionProps> = ({ html, isCorrect, isSelected, multi, keyHidden = false }) => {
    const showCorrect = !keyHidden && isCorrect;
    const isSelectedWrong = !keyHidden && isSelected && !isCorrect;
    const variant = showCorrect ? 'correct' : isSelectedWrong ? 'incorrect' : 'neutral';
    const pickedNeutral = keyHidden && isSelected;
    return (
        <div
            className={cn(
                'ts-review-opt',
                variant === 'correct' && 'ts-review-opt-correct',
                variant === 'incorrect' && 'ts-review-opt-incorrect',
            )}
        >
            {multi ? (
                <span
                    className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border mt-0.5 transition-colors',
                        variant === 'correct'
                            ? 'border-green-600 bg-green-600 text-white dark:border-green-500 dark:bg-green-500'
                            : variant === 'incorrect'
                              ? 'border-red-600 bg-red-600 text-white dark:border-red-500 dark:bg-red-500'
                              : pickedNeutral
                                ? 'border-foreground bg-foreground text-background'
                                : 'border-gray-300 dark:border-gray-600 bg-transparent',
                    )}
                >
                    {(showCorrect || isSelected) && (
                        <Check className={cn('h-3 w-3', pickedNeutral ? 'text-background' : 'text-white')} strokeWidth={3} />
                    )}
                </span>
            ) : (
                <span
                    className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border mt-0.5 transition-colors',
                        variant === 'correct'
                            ? 'border-green-600 dark:border-green-500 bg-transparent'
                            : variant === 'incorrect'
                              ? 'border-red-600 dark:border-red-500 bg-transparent'
                              : pickedNeutral
                                ? 'border-foreground bg-transparent'
                                : 'border-gray-300 dark:border-gray-600 bg-transparent',
                    )}
                >
                    {showCorrect && <span className="block h-2 w-2 rounded-full bg-green-600 dark:bg-green-500" />}
                    {isSelectedWrong && <span className="block h-2 w-2 rounded-full bg-red-600 dark:bg-red-500" />}
                    {pickedNeutral && <span className="block h-2 w-2 rounded-full bg-foreground" />}
                </span>
            )}
            <span className="ts-opt-text">
                <RenderedHtml html={html} inline />
            </span>
            <span className="ts-review-opt-icon">
                {showCorrect && <CheckCircle2 size={16} className="text-green-500" />}
                {isSelectedWrong && <XCircle size={16} className="text-red-500" />}
            </span>
        </div>
    );
};

interface ReviewFillProps {
    state: ReviewState;
    given: React.ReactNode;
    correct: React.ReactNode | null;
}

/** A typed answer, followed by the correct one when it was missed. */
export const ReviewFill: React.FC<ReviewFillProps> = ({ state, given, correct }) => {
    const { t } = useTranslation();
    return (
        <div className="space-y-2">
            <div
                className={cn(
                    'ts-review-fill',
                    state === 'empty'
                        ? 'ts-review-fill-empty'
                        : state === 'correct'
                          ? 'ts-review-fill-correct'
                          : 'ts-review-fill-incorrect',
                )}
            >
                <p className="ts-review-fill-label">{t('your_answer')}</p>
                <div className="ts-review-fill-value">{state === 'empty' ? '—' : given}</div>
            </div>
            {state !== 'correct' && correct != null && (
                <div className="ts-review-fill ts-review-fill-correct">
                    <p className="ts-review-fill-label">{t('correct_answer')}</p>
                    <div className="ts-review-fill-value">{correct}</div>
                </div>
            )}
        </div>
    );
};

interface ReviewMatchRowProps {
    state: ReviewState;
    label: React.ReactNode;
    given: React.ReactNode;
    correct: React.ReactNode;
}

/** One matching pair: the prompt, the learner's match, and the correction when it differs. */
export const ReviewMatchRow: React.FC<ReviewMatchRowProps> = ({ state, label, given, correct }) => {
    const { t } = useTranslation();
    return (
        <div className="ts-review-match-row">
            <div className="ts-review-match-key">{label}</div>
            <span className="ts-review-match-eq">=</span>
            <div
                className={cn(
                    'ts-review-match-val',
                    state === 'empty' && 'ts-review-match-val-empty',
                    state === 'correct' && 'ts-review-match-val-correct',
                    state === 'incorrect' && 'ts-review-match-val-incorrect',
                )}
            >
                {state === 'empty' ? '—' : given}
                {state === 'incorrect' && <span className="ts-review-match-correction">→ {correct}</span>}
                {state === 'empty' && (
                    <span className="ts-review-match-correction text-xs opacity-90 ml-1">
                        ({t('correct_answer')}: {correct})
                    </span>
                )}
            </div>
        </div>
    );
};

/** The author's explanation: closed until the reader asks for it, so a long review stays scannable. */
export const ReviewExplanation: React.FC<{ html: string; className?: string }> = ({ html, className = 'mt-5' }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const panelId = useId();
    return (
        <div className={cn('rounded-xl border border-amber-200/80 bg-amber-50/50 dark:border-amber-900/50 dark:bg-amber-950/30', className)}>
            <button
                type="button"
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left text-xs font-semibold text-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-200"
            >
                <Lightbulb className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                <span className="min-w-0 flex-1">{t('explanation')}</span>
                <ChevronDown
                    className={cn('h-4 w-4 shrink-0 text-amber-700/70 transition-transform dark:text-amber-300/70', open && 'rotate-180')}
                    aria-hidden
                />
            </button>
            {open && (
                <div
                    id={panelId}
                    className="mx-4 border-t border-amber-200/60 pb-4 pt-2.5 text-sm leading-relaxed text-foreground/90 dark:border-amber-900/40"
                >
                    <RenderedHtml html={html} />
                </div>
            )}
        </div>
    );
};
